import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  Image,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Dimensions,
  RefreshControl,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

import { Linking } from 'react-native';


const TOP_BADGE = new Set(['AAA','AA','A']);

// Simplified follower buckets
const FOLLOWER_BUCKETS = [
  { key: '10k–25k', min: 10000, max: 25000 },
  { key: '25k–50k', min: 25000, max: 50000 },
  { key: '50k–100k', min: 50000, max: 100000 },
  { key: '100k–300k', min: 100000, max: 300000 },
  { key: '300k+',   min: 300000, max: Infinity },
];

const parseFollowers = (str = '') => {
  const s = String(str).toLowerCase().replace(/,/g, '').trim();
  if (s.endsWith('k')) return parseFloat(s) * 1000;
  if (s.endsWith('m')) return parseFloat(s) * 1_000_000;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

// Show up to 60 creators after filters/sort
const MAX_RENDER = 50;

export default function BrowseInviteCreators() {
  const navigation = useNavigation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');

  // Raw data
  const [suggested, setSuggested] = useState([]);
  const [pool, setPool] = useState([]);
  const mounted = useRef(false);

  // Filters
  const [categories, setCategories] = useState(['All']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [bucket, setBucket] = useState(null);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [suggestedOnly, setSuggestedOnly] = useState(false);
  const [query, setQuery] = useState('');

  // Sort
  const [sort, setSort] = useState('shuffle'); // followers_desc | followers_asc | az | shuffle

  const fetchAll = async () => {
    const dd = new Date();
    const ymd = dd.toISOString().slice(0,10).replace(/-/g,''); // cache-buster
    const urls = [
      `https://collabbayadmin.com/APIControllers/GetSuggestedUsersForBrand.php?d=${ymd}`,
      `https://collabbayadmin.com/APIControllers/GetVenueBrowseInviteOptions.php?d=${ymd}`,
    ];
    const [r1, r2] = await Promise.all([
      fetch(urls[0], { method: 'GET', credentials: 'include' }),
      fetch(urls[1], { method: 'GET', credentials: 'include' }),
    ]);
    if (!r1.ok) throw new Error(`Suggested HTTP ${r1.status}`);
    if (!r2.ok) throw new Error(`Pool HTTP ${r2.status}`);
    const j1 = await r1.json();
    const j2 = await r2.json();

    // Suggested may come in "data" or "users" depending on your version; prefer "data"
    const s = Array.isArray(j1?.data) ? j1.data
            : Array.isArray(j1?.users) ? j1.users
            : [];
    const p = Array.isArray(j2?.pool) ? j2.pool
            : Array.isArray(j2?.data) ? j2.data.filter(i => !i.isSuggested)
            : [];

    // Mark suggested if endpoint didn't
    const sMarked = s.map(x => ({ ...x, isSuggested: true }));

    // One-time shuffle for extra freshness each fetch
    const shuffleArr = (arr) => {
      const a = arr.slice();
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    setSuggested(shuffleArr(sMarked));
    setPool(shuffleArr(p));

    // Build categories
    const uniqueCats = ['All', ...Array.from(new Set([...sMarked, ...p].map(i => i.ContentCategories).filter(Boolean)))];
    setCategories(uniqueCats);
  };

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        await fetchAll();
        setLoading(false);
      } catch (e) {
        console.error(e);
        setErr('Could not load creators right now.');
        setSuggested([]);
        setPool([]);
        setCategories(['All']);
        setLoading(false);
      }
    })();
  }, []);

  // Pull-to-refresh
  const onRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchAll();
    } catch (e) {
      console.error(e);
      setErr('Refresh failed. Try again.');
    } finally {
      setRefreshing(false);
    }
  };


const [isAuthenticated, setIsAuthenticated] = useState(false);

useEffect(() => {
  fetch('https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
    .then(res => res.json())
    .then(data => setIsAuthenticated(data.authenticated === true))
    .catch(() => setIsAuthenticated(false));
}, []);


  // Working list (filters + sort applied to combined list, but “For you” strip displays separately)
  const combined = useMemo(() => {
    let base = suggestedOnly ? suggested.slice() : [...suggested, ...pool];

    // Search by username
    const q = query.trim().toLowerCase();
    if (q) base = base.filter(i => String(i.Username || '').toLowerCase().includes(q));

    // Category
    if (selectedCategory !== 'All') {
      base = base.filter(i => String(i.ContentCategories).trim() === selectedCategory);
    }

    // Follower bucket
    if (bucket) {
      base = base.filter(i => {
        const n = parseFollowers(i.PlatformFollowers);
        return n >= bucket.min && n < bucket.max;
      });
    }

    // Photo
    if (hasPhoto) {
      base = base.filter(i => !!i.ProfileImage);
    }

    // Sort
    if (sort === 'followers_desc') {
      base.sort((a, b) => parseFollowers(b.PlatformFollowers) - parseFollowers(a.PlatformFollowers));
    } else if (sort === 'followers_asc') {
      base.sort((a, b) => parseFollowers(a.PlatformFollowers) - parseFollowers(b.PlatformFollowers));
    } else if (sort === 'az') {
      base.sort((a, b) => String(a.Username).localeCompare(String(b.Username)));
    } else if (sort === 'shuffle') {
      for (let i = base.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [base[i], base[j]] = [base[j], base[i]];
      }
    }

// Cap to max 5 creators with >= 50k followers
const out = [];
let big = 0;

for (const item of base) {
  const n = parseFollowers(item.PlatformFollowers);
  if (n >= 50000) {
    if (big >= 5) continue; // skip extras above the cap
    big++;
  }
  out.push(item);
  if (out.length >= MAX_RENDER) break;
}

return out;


  }, [suggested, pool, suggestedOnly, selectedCategory, bucket, hasPhoto, sort, query]);

  if (loading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color="#000" />
      </View>
    );
  }

  const showEmpty = combined.length === 0;
  const deviceW = Dimensions.get('window').width;

  return (
    <ScrollView
      contentContainerStyle={styles.scrollContainer}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
    >
      <View style={styles.contentWrapper}>

        <View style={{ position: 'absolute', top: 15, right: 15, zIndex: 999 }}>
  <TouchableOpacity onPress={() => Linking.openURL('https://collabbayadmin.com')}>
    <Ionicons name="close" size={28} color="black" />
  </TouchableOpacity>
</View>

        {/* Header */}
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color="black" />
        </TouchableOpacity>

        <Text style={styles.header}>Looking for more bookings?</Text>
        <Text style={styles.subhead}>Propose invites to creators through collabbay.</Text>

{Math.random() < 0.3 && (
  <View style={styles.inviteInfoBox}>
    <Text style={styles.inviteInfoHeader}>💡 Pro tip</Text>
    <Text style={styles.inviteInfoText}>
      Most venues start by sending a few invites — it’s a quick way to get the ball rolling
      and build early traction with creators.
    </Text>
  </View>
)}


        {/* Search */}
        <View style={styles.searchBar}>
          <Ionicons name="search" size={18} color="#666" style={{ marginRight: 8 }} />
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Search username..."
            placeholderTextColor="#999"
            style={styles.searchInput}
            autoCapitalize="none"
          />
          {query ? (
            <TouchableOpacity onPress={() => setQuery('')}>
              <Ionicons name="close-circle" size={18} color="#999" />
            </TouchableOpacity>
          ) : null}
        </View>

        {/* “For you” Suggested Strip */}
        {!suggestedOnly && suggested.length > 0 && (
          <>
            <View style={styles.sectionHeader}>
              <Ionicons name="star-outline" size={18} color="#000" />
              <Text style={styles.sectionTitle}>For you</Text>
            </View>

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ paddingRight: 8 }}
              style={{ marginBottom: 10 }}
            >
         
{suggested.map((cr, idx) => (
  <TouchableOpacity
    key={`sg-${cr.Username}-${idx}`}
    onPress={() => {
      if (!isAuthenticated) {
        Linking.openURL('https://collabbayadmin.com/subscribe');
      } else {
        navigation.navigate('InviteCollabbayCreator', { Username: cr.Username });
      }
    }}
    style={[styles.suggestCard, { width: Math.min(240, deviceW * 0.72) }]}
    activeOpacity={0.9}
  >
    <Image source={{ uri: cr.ProfileImage }} style={styles.suggestImage} />
    <View style={styles.suggestOverlay}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text numberOfLines={1} style={styles.suggestName}>
          {!isAuthenticated ? (
            <>
              {cr.Username?.charAt(0)}
              <Text style={styles.blurredText}>{cr.Username?.slice(1)}</Text>
            </>
          ) : (
            cr.Username
          )}
        </Text>
        {TOP_BADGE.has(String(cr.CreatorRating || '').toUpperCase()) && (
          <View style={styles.topIconPill}>
            <Ionicons name="ribbon" size={12} color="#000" />
          </View>
        )}
        <View style={[styles.badge, styles.badgeMuted, { marginLeft: 6 }]}>
          <Text style={styles.badgeText}>Suggested</Text>
        </View>
      </View>
      <Text numberOfLines={1} style={styles.suggestMeta}>
        {formatBucket(cr.PlatformFollowers)} • {String(cr.ContentCategories || '').trim() || 'Creator'} Creator
      </Text>
    </View>
  </TouchableOpacity>
))}


            </ScrollView>
          </>
        )}

        {/* Sort */}
        <View style={styles.rowWrap}>
          <Chip label="Followers ↑" active={sort === 'followers_desc'} onPress={() => setSort('followers_desc')} />
          <Chip label="Followers ↓" active={sort === 'followers_asc'} onPress={() => setSort('followers_asc')} />
          <Chip label="A–Z" active={sort === 'az'} onPress={() => setSort('az')} />
          <Chip label="Shuffle" active={sort === 'shuffle'} onPress={() => setSort('shuffle')} />
          <Chip label="Reset" active={false} onPress={() => { setSelectedCategory('All'); setBucket(null); setHasPhoto(false); setSuggestedOnly(false); setQuery(''); setSort('shuffle'); }} />
        </View>

        {/* Filters */}
        <Text style={styles.filterHeading}>Filter</Text>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 8 }}>
          {categories.map((cat, i) => (
            <Chip
              key={`${cat}-${i}`}
              label={cat}
              active={selectedCategory === cat}
              onPress={() => setSelectedCategory(cat)}
            />
          ))}
        </ScrollView>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 8 }}>
          {FOLLOWER_BUCKETS.map((b) => (
            <Chip
              key={b.key}
              label={b.key}
              active={bucket?.key === b.key}
              onPress={() => setBucket(bucket?.key === b.key ? null : b)}
            />
          ))}
        </ScrollView>

        <View style={styles.toggleRow}>
          <Toggle active={hasPhoto} icon="image-outline" label="Has photo" onPress={() => setHasPhoto(v => !v)} />
          <Toggle active={suggestedOnly} icon="star-outline" label="Suggested only" onPress={() => setSuggestedOnly(v => !v)} />
          <View style={{ flex: 1 }} />
          <Text style={styles.countText}>Showing {combined.length} / {MAX_RENDER}</Text>
        </View>

        {/* Main list */}
        <View style={styles.sectionHeader}>
          <Ionicons name="people-outline" size={18} color="#000" />
          <Text style={styles.sectionTitle}>{suggestedOnly ? 'Suggested creators' : 'More creators'}</Text>
        </View>

        {showEmpty ? (
          <View style={{ alignItems: 'center', marginTop: 24 }}>
            <Text style={styles.emptyMsg}>
              {err || 'No matches. Try loosening filters or post an offer to invite creators.'}
            </Text>
            <TouchableOpacity
              style={styles.ctaBtn}
              onPress={() => navigation.navigate('CreateBrandListing')}
            >
              <Text style={styles.ctaText}>
                Create an offer  <Ionicons name="arrow-forward" size={18} color="#000" />
              </Text>
            </TouchableOpacity>
          </View>
     ) : (
  combined.map((cr, idx) => (
    <CreatorCard
      key={`cr-${cr.Username}-${idx}`}
      creator={cr}
      isAuthenticated={isAuthenticated}
    />
  ))
)}


        {!showEmpty && combined.length < MAX_RENDER && (
          <Text style={styles.nudge}>Tip: widen filters to see more creators (up to {MAX_RENDER} shown).</Text>
        )}
      </View>
    </ScrollView>
  );
}

function CreatorCard({ creator, isAuthenticated }) {
  const navigation = useNavigation();
  const top = TOP_BADGE.has(String(creator.CreatorRating || '').toUpperCase());

  const handlePress = () => {
    if (!isAuthenticated) {
      Linking.openURL('https://collabbayadmin.com/subscribe');
    } else {
      navigation.navigate('InviteCollabbayCreator', { Username: creator.Username });
    }
  };


  return (
    <TouchableOpacity style={styles.card} onPress={handlePress} activeOpacity={0.9}>
      <View style={styles.row}>
        <View style={{ position: 'relative' }}>
          <Image source={{ uri: creator.ProfileImage }} style={styles.image} />
          <View pointerEvents="none" style={styles.haloOverlay} />
        </View>
        <View style={styles.details}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text style={styles.name} numberOfLines={1}>
              {!isAuthenticated ? (
                <>
                  {creator.Username?.charAt(0)}
                  <Text style={styles.blurredText}>{creator.Username?.slice(1)}</Text>
                </>
              ) : (
                creator.Username
              )}
            </Text>
            {top && (
              <View style={styles.topIconPill}>
                <Ionicons name="ribbon" size={12} color="#000" />
              </View>
            )}
            {creator.isSuggested && (
              <View style={[styles.badge, styles.badgeMuted, { marginLeft: 6 }]}>
                <Text style={styles.badgeText}>Suggested</Text>
              </View>
            )}
          </View>
          <Text style={styles.followers}>{formatBucket(creator.PlatformFollowers)} followers</Text>
          <Text style={styles.category} numberOfLines={1}>
            {String(creator.ContentCategories || '').trim() || 'Creator'}
          </Text>
        </View>
        <TouchableOpacity style={styles.viewButton}>
          <Ionicons name="arrow-forward" size={20} color="#000" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
}


function Chip({ label, active, onPress }) {
  return (
    <TouchableOpacity onPress={onPress} style={[styles.chip, active && styles.activeChip]} activeOpacity={0.85}>
      <Text style={styles.chipText}>{label}</Text>
    </TouchableOpacity>
  );
}

function Toggle({ active, onPress, icon, label }) {
  return (
    <TouchableOpacity onPress={onPress} style={[styles.toggleBtn, active && styles.activeToggle]} activeOpacity={0.9}>
      <Ionicons name={icon} size={16} color="#000" />
      <Text style={styles.toggleText}>{label}</Text>
    </TouchableOpacity>
  );
}

function formatBucket(countStr) {
  const n = parseFollowers(countStr);
  if (n < 25000) return '10k–25k';
  if (n < 50000) return '25k–50k';
  if (n < 100000) return '50k–100k';
  if (n < 300000) return '100k–300k';
  return '300k+';
}

const styles = StyleSheet.create({
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  scrollContainer: { padding: 20, backgroundColor: '#fff', alignItems: 'center' },
  contentWrapper: { width: '100%', maxWidth: 600 },

  backButton: {
    marginBottom: 15,
    alignSelf: 'flex-start',
    backgroundColor: '#eee',
    padding: 8,
    borderRadius: 30,
  },

  header: { fontSize: 29, fontWeight: 'bold', marginBottom: 6, color: '#000' },
  subhead: { fontSize: 15, color: '#555', marginBottom: 12, fontWeight: '500' },

  // Search
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#e6e6e6',
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  searchInput: { flex: 1, fontSize: 14, color: '#111' },

  sectionHeader: { flexDirection: 'row', alignItems: 'center', marginTop: 8, marginBottom: 8 },
  sectionTitle: { marginLeft: 8, fontSize: 14, fontWeight: '800', color: '#000' },

  // Suggested horizontal cards
  suggestCard: {
    height: 150,
    borderRadius: 18,
    marginRight: 12,
    overflow: 'hidden',
    backgroundColor: '#f3f3f3',
    borderWidth: 1,
    borderColor: '#eee',
  },
  suggestImage: { width: '100%', height: '100%' },

  // Translucent footer (no blur)
  suggestOverlay: {
    position: 'absolute',
    left: 0, right: 0, bottom: 0,
    paddingVertical: 8, paddingHorizontal: 10,
    backgroundColor: 'rgba(255,255,255,0.78)',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.6)',
  },

  suggestName: { fontSize: 14, fontWeight: '800', color: '#000', maxWidth: '60%' },
  suggestMeta: { fontSize: 12, color: '#222', marginTop: 2 },

  // Filters (Google-ish chips)
  rowWrap: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8 },
  filterHeading: { fontSize: 13, fontWeight: '700', color: '#000', marginTop: 2, marginBottom: 6 },

  chip: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 18,
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#e6e6e6',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  activeChip: {
    backgroundColor: '#fdf05d',
    borderColor: '#f0e04a',
  },
  chipText: { fontSize: 13, fontWeight: '600', color: '#000' },

  toggleRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  toggleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 18,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e6e6e6',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  activeToggle: {
    backgroundColor: '#fdf05d',
    borderColor: '#f0e04a',
  },
  toggleText: { fontSize: 13, fontWeight: '600', color: '#000', marginLeft: 6 },

  countText: { fontSize: 12, color: '#666', fontWeight: '600' },

  // Main cards
  card: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 14,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#eee',
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  row: { flexDirection: 'row', alignItems: 'center' },
  image: {
    width: 110,
    height: 110,
    borderRadius: 16,
    backgroundColor: '#e9e9e9',
    marginRight: 14,
  },

  // Halo overlay (no blur)
  haloOverlay: {
    position: 'absolute',
    left: -6, right: -6, bottom: -6, top: -6,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },

  details: { flex: 1 },

  name: { fontSize: 16, fontWeight: '800', color: '#111' },
  followers: { fontSize: 14, fontWeight: '600', color: '#333', marginTop: 4 },
  category: { fontSize: 12, color: '#666', marginTop: 2 },

  viewButton: {
    backgroundColor: '#fdf05d',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
  },

  // Icon-only "Top" pill
  topIconPill: {
    marginLeft: 8,
    backgroundColor: '#fdf05d',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },

  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  badgeMuted: {
    backgroundColor: '#eee',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#000',
  },

  blurredText: {
  color: 'transparent',
  textShadowColor: 'rgba(0,0,0,0.3)',
  textShadowOffset: { width: 0, height: 0 },
  textShadowRadius: 5,
  userSelect: 'none',
},


  ctaBtn: {
    backgroundColor: '#fdf05d',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 25,
    marginTop: 10,
  },
  ctaText: { fontWeight: 'bold', color: '#000', fontSize: 18 },

  emptyMsg: { fontSize: 15, color: '#555', textAlign: 'center' },

  nudge: { marginTop: 6, fontSize: 12, color: '#666', textAlign: 'center' },
});
